import type { EventName, EventProperties } from "@/api/posthog";

/**
 * Tracking contract of the availability modal.
 *
 * **Why the event name does not change.** `update_availability_from_layout` has been
 * collected for months; renaming it to say «from anywhere» would split the series in two
 * and make the before/after of this redesign unmeasurable. The name stays; what it was
 * missing was not a better noun but an origin.
 *
 * **Why the origin is a closed set of three.** The modal is mounted once by the layout
 * and opened from three surfaces: the navigation bars (desktop, mobile and the auth area),
 * the dashboard header's chip, and the call to action inside the visibility tile. The last
 * two say «set your availability» within one screen of each other, which is exactly why
 * each has to be distinguishable: without the origin the funnel cannot tell which of them
 * is the one that works. The FLOW (E12) also named the profile sidebar — it has no
 * availability button, so that value would be an origin no event will ever carry, and the
 * type is what says so. `dashboard_notice` was a fourth: the banner it belonged to is
 * gone, and a value nothing can emit is a value that makes the funnel harder to read.
 */
export const AVAILABILITY_EVENTS = {
  save: "update_availability_from_layout",
} as const satisfies Record<string, EventName>;

export const AVAILABILITY_SOURCES = [
  "navigation_bar",
  "dashboard_header",
  "dashboard_tile",
] as const;

export type AvailabilitySource = (typeof AVAILABILITY_SOURCES)[number];

/** Compile-time guard: flat scalars only — PostHog rejects nesting. */
type CapturePayload<T extends EventProperties> = T;

export type AvailabilitySaveProps = CapturePayload<{
  source: AvailabilitySource | null;
}>;

/**
 * `null` is «opened by a caller that did not declare itself», which must not happen and
 * is not the same fact as «opened from the navigation bar». Defaulting to a real surface
 * would make the payload lie, and the lie would be invisible in the funnel.
 */
export const availabilitySaveProps = (
  source: AvailabilitySource | null,
): AvailabilitySaveProps => ({ source });
